/**
 * SearchSelect – durchsuchbares Auswahlfeld (eigenes Popup, Desktop-Variante).
 *
 * Ersetzt native <select>-/<datalist>-Felder in der Frontend-Zeiterfassung.
 * Rendert ein eigenes Dropdown aus DOM-Elementen (themenkonform über die
 * Paletten-CSS-Variablen) mit Tippsuche.
 *
 * Unterschied zur PWA-Variante: Das Frontend läuft am Desktop ohne
 * Bildschirmtastatur, die den Viewport verkleinert. Daher wird die
 * Öffnungsrichtung GEMESSEN (bevorzugt nach unten, nach oben nur wenn unten
 * kein Platz ist) – ohne das Feld beim Öffnen nach oben zu scrollen, das wäre
 * am Desktop störend.
 *
 * Anzeige-Logik: Beim Tippen wird die Sucheingabe (`query`) angezeigt, sonst
 * die aktuelle Auswahl (`selectedLabel`, abgeleitet aus dem `value`-Prop) –
 * kein manuelles Zurücksetzen, daher keine Timing-/Stale-State-Probleme.
 *
 * Props-API identisch zur vorherigen Datalist-Variante – Aufrufstellen
 * unverändert.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'

export interface SearchSelectOption {
  /** Technischer Wert (z. B. ID), wird nach außen über onChange geliefert. */
  value: string
  /** Sichtbarer Anzeigetext. */
  label: string
}

export interface SearchSelectProps {
  /** Aktuell gewählter Option-Wert (id) oder ''. */
  value: string
  /** Liefert den Wert der gewählten Option bzw. '' beim Leeren. */
  onChange: (value: string) => void
  options: SearchSelectOption[]
  placeholder?: string
  className?: string
  id?: string
  disabled?: boolean
  required?: boolean
  /** Hinweistext, wenn der eingegebene Text zu keiner Option passt. */
  noMatchHint?: string
  'aria-label'?: string
}

export function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
  id,
  disabled,
  required,
  noMatchHint = 'Kein passender Eintrag gefunden',
  'aria-label': ariaLabel,
}: SearchSelectProps) {
  const autoId = useId()
  const inputId = id ?? `ss-${autoId}`
  const listboxId = `${inputId}-listbox`

  const [query, setQuery] = useState('')
  const [typing, setTyping] = useState(false)
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [dropUp, setDropUp] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const closeTimer = useRef<number | null>(null)

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value],
  )

  // Beim Tippen die Sucheingabe anzeigen, sonst die aktuelle Auswahl.
  const display = typing ? query : selectedLabel

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!typing || q === '') return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query, typing])

  // Öffnungsrichtung messen: bevorzugt nach unten; nach oben nur, wenn unten
  // weniger als die Listenhöhe passt UND oben mehr Platz ist.
  const recompute = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const vh = window.innerHeight
    const below = vh - rect.bottom
    const above = rect.top
    setDropUp(below < 260 && above > below)
  }, [])

  useEffect(() => {
    if (!open) return
    recompute()
    window.addEventListener('resize', recompute)
    window.addEventListener('scroll', recompute, true)
    return () => {
      window.removeEventListener('resize', recompute)
      window.removeEventListener('scroll', recompute, true)
    }
  }, [open, recompute])

  function clearTimer() {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  function commit(opt: SearchSelectOption) {
    onChange(opt.value)
    setQuery('')
    setTyping(false)
    setOpen(false)
  }

  function handleInput(v: string) {
    setTyping(true)
    setQuery(v)
    setHighlight(0)
    setOpen(true)
    if (v === '') onChange('') // Leeren = Auswahl aufheben
  }

  function handleBlur() {
    // Verzögert schließen, damit der Klick auf eine Option noch verarbeitet
    // wird. Kein Zurücksetzen des Textes nötig – `display` ist reaktiv.
    clearTimer()
    closeTimer.current = window.setTimeout(() => {
      setOpen(false)
      setTyping(false)
      setQuery('')
    }, 120)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setOpen(true)
      setHighlight((h) => Math.min(h + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      // Hervorgehobenen Eintrag übernehmen; bleibt nur ein Treffer übrig,
      // ist das automatisch dieser (highlight wird bei jedem Tippen auf 0
      // gesetzt) – so genügt Enter, sobald die Liste auf einen Eintrag gefiltert ist.
      const pick = filtered[highlight] ?? (filtered.length === 1 ? filtered[0] : undefined)
      if (open && pick) {
        e.preventDefault()
        commit(pick)
      }
    } else if (e.key === 'Tab') {
      // Genau ein gefilterter Treffer → mit Tab übernehmen und regulär zum
      // nächsten Feld weiterspringen (kein preventDefault).
      if (open && typing && filtered.length === 1) {
        commit(filtered[0])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setTyping(false)
      setQuery('')
    }
  }

  const showNoMatch = open && typing && query.trim() !== '' && filtered.length === 0

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={inputId}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        aria-label={ariaLabel}
        value={display}
        className={className}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => {
          clearTimer()
          setOpen(true)
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />

      {open && filtered.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          onMouseDown={(e) => e.preventDefault()}
          className={
            'absolute left-0 right-0 z-50 max-h-60 overflow-auto rounded-md border bg-popover py-1 text-popover-foreground shadow-lg ' +
            (dropUp ? 'bottom-full mb-1' : 'top-full mt-1')
          }
        >
          {filtered.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              onClick={() => commit(o)}
              onMouseEnter={() => setHighlight(i)}
              className={
                'cursor-pointer px-3 py-2 text-sm ' +
                (i === highlight ? 'bg-accent text-accent-foreground' : '')
              }
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}

      {showNoMatch && noMatchHint && (
        <p className="mt-1 text-xs text-muted-foreground">{noMatchHint}</p>
      )}
    </div>
  )
}
